// ========= START: bp-tango-dev/src/components/TangoPracticeScreen.js (ADDED CLOSE X) =========
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react'; // Requires: npm install qrcode.react
import Flashcard from './Flashcard';
import SideMenu from './SideMenu';
import speechSynth from '../utils/speechSynth';
import { CATEGORIES as MasterCategoryList } from './TangoSetupScreen';
import DynamicTextLabel from './DynamicTextLabel';

const shuffleArray = (array) => {
  let currentIndex = array.length, randomIndex;
  const newArray = [...array];
  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    [newArray[currentIndex], newArray[randomIndex]] = [newArray[randomIndex], newArray[currentIndex]];
  }
  return newArray;
};

const formatTime = (timeInSeconds) => {
  const minutes = Math.floor(timeInSeconds / 60).toString().padStart(2, '0');
  const seconds = (timeInSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
};

function TangoPracticeScreen({ settings, onEndPractice }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isShuffled, setIsShuffled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeCategories, setActiveCategories] = useState(settings.categories);
  const [speechSpeed, setSpeechSpeed] = useState('normal');
  const [displayMode, setDisplayMode] = useState('single');
  const [flippedStates, setFlippedStates] = useState({});

  // Game State
  const [isSpeakingRandomly, setIsSpeakingRandomly] = useState(false);
  const [targetWordId, setTargetWordId] = useState(null);

  // Timer
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerIsActive, setTimerIsActive] = useState(false);
  const startTimeRef = useRef(0);
  const currentCategoryRef = useRef(null);

  // Layout
  const multiViewContainerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [dynamicGridStyles, setDynamicGridStyles] = useState({ gridTemplateColumns: '', cardWidth: 0, cardHeight: 0, maxLabelHeight: 0 });

  // --- NEW: Selection / Make Set Mode State ---
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedWordIds, setSelectedWordIds] = useState([]);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrData, setQrData] = useState('');

  const originalWordOrder = useMemo(() => settings.words, [settings.words]);

  const displayWords = useMemo(() => {
    const filtered = originalWordOrder.filter(word => activeCategories.includes(word.category));

    if (isShuffled) {
      return shuffleArray(filtered);
    } else {
      return filtered.sort((a, b) => {
        const indexA = MasterCategoryList.findIndex(cat => cat.id === a.category);
        const indexB = MasterCategoryList.findIndex(cat => cat.id === b.category);
        if (indexA !== indexB) return indexA - indexB;
        return 0;
      });
    }
  }, [originalWordOrder, activeCategories, isShuffled]);

  const currentCategoryWords = useMemo(() => {
    if (!displayWords[currentIndex]) return [];
    const currentCategory = displayWords[currentIndex].category;
    return displayWords.filter(word => word.category === currentCategory);
  }, [currentIndex, displayWords]);

  const practiceCategoriesDetails = useMemo(() => {
    return MasterCategoryList.filter(cat => settings.categories.includes(cat.id));
  }, [settings.categories]);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;
        setContainerSize({ width, height });
      }
    });
    const currentRef = multiViewContainerRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [displayMode]);

  useEffect(() => {
    if (displayMode !== 'multi' || containerSize.width === 0 || currentCategoryWords.length === 0) return;

    const calculateBestLayout = () => {
      const cardCount = currentCategoryWords.length;
      const containerWidth = containerSize.width;
      const containerHeight = containerSize.height;
      let bestLayout = { cols: 0, cardSize: 0 };
      const rowGap = 15;
      const colGap = 10;
      const CARD_TO_TOTAL_HEIGHT_RATIO = 1.55;

      for (let rows = 1; rows <= cardCount; rows++) {
        const cols = Math.ceil(cardCount / rows);
        const totalVerticalGap = (rows - 1) * rowGap;
        const cardSizeFromHeight = (containerHeight - totalVerticalGap) / (rows * CARD_TO_TOTAL_HEIGHT_RATIO);
        const totalHorizontalGap = (cols - 1) * colGap;
        const cardSizeFromWidth = (containerWidth - totalHorizontalGap) / cols;
        const currentCardSize = Math.min(cardSizeFromHeight, cardSizeFromWidth);

        if (currentCardSize > bestLayout.cardSize) {
          bestLayout = { cols: cols, cardSize: currentCardSize };
        }
      }
      const maxLabelHeight = bestLayout.cardSize * (CARD_TO_TOTAL_HEIGHT_RATIO - 1);

      setDynamicGridStyles({
        gridTemplateColumns: `repeat(${bestLayout.cols}, ${bestLayout.cardSize}px)`,
        cardWidth: bestLayout.cardSize,
        cardHeight: bestLayout.cardSize,
        maxLabelHeight: maxLabelHeight,
      });
    };
    calculateBestLayout();
  }, [containerSize, currentCategoryWords, displayMode]);

  useEffect(() => {
    let interval = null;
    if (timerIsActive) {
      startTimeRef.current = Date.now() - elapsedTime * 1000;
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerIsActive, elapsedTime]);

  useEffect(() => {
    if (displayMode !== 'multi' || currentCategoryWords.length === 0 || !timerIsActive) return;
    const allFlipped = currentCategoryWords.every(word => flippedStates[word.id]);
    if (allFlipped) {
      setTimerIsActive(false);
      setIsSpeakingRandomly(false);
      setTargetWordId(null);
    }
  }, [flippedStates, currentCategoryWords, displayMode, timerIsActive]);

  const categoryId = currentCategoryWords[0]?.category;
  useEffect(() => {
    if (displayMode === 'multi' && categoryId && currentCategoryRef.current !== categoryId) {
      setTimerIsActive(false);
      setElapsedTime(0);
      setFlippedStates({});
      setIsSpeakingRandomly(false);
      setTargetWordId(null);
      currentCategoryRef.current = categoryId;
    }
  }, [categoryId, displayMode]);

  useEffect(() => {
    if (!isSpeakingRandomly || displayMode !== 'multi') {
      setTargetWordId(null);
      return;
    }
    const runGameLoop = () => {
      const faceUpWords = currentCategoryWords.filter(word => !flippedStates[word.id]);
      if (faceUpWords.length === 0) {
        setIsSpeakingRandomly(false);
        setTargetWordId(null);
        return;
      }
      let wordToSpeakObj = null;
      const currentTargetIsValid = targetWordId && faceUpWords.some(w => w.id === targetWordId);
      if (currentTargetIsValid) {
        wordToSpeakObj = faceUpWords.find(w => w.id === targetWordId);
      } else {
        const randomIndex = Math.floor(Math.random() * faceUpWords.length);
        wordToSpeakObj = faceUpWords[randomIndex];
        setTargetWordId(wordToSpeakObj.id);
      }
      if (wordToSpeakObj) speechSynth.speak(wordToSpeakObj.word, speechSpeed);
    };
    runGameLoop();
    const intervalId = setInterval(runGameLoop, 2000);
    return () => clearInterval(intervalId);
  }, [isSpeakingRandomly, displayMode, flippedStates, currentCategoryWords, speechSpeed, targetWordId]);

  useEffect(() => { if (currentIndex >= displayWords.length && displayWords.length > 0) setCurrentIndex(0); }, [displayWords, currentIndex]);

  const handleNext = () => { if (displayWords.length > 0) setCurrentIndex(p => (p + 1) % displayWords.length); };
  const handlePrevious = () => { if (displayWords.length > 0) setCurrentIndex(p => (p - 1 + displayWords.length) % displayWords.length); };

  const handleSayWord = () => {
    if (isSelectionMode) return; // Disable sound/game in selection mode
    if (displayMode === 'single' && displayWords[currentIndex]) {
      speechSynth.speak(displayWords[currentIndex].word, speechSpeed);
    } else if (displayMode === 'multi') {
      const allCardsFlipped = currentCategoryWords.length > 0 && currentCategoryWords.every(word => flippedStates[word.id]);
      if (allCardsFlipped) return;
      const newSpeakingState = !isSpeakingRandomly;
      setIsSpeakingRandomly(newSpeakingState);
      setTimerIsActive(newSpeakingState);
      if (!newSpeakingState) setTargetWordId(null);
    }
  };

  const handleToggleMultiMode = () => {
    setIsSpeakingRandomly(false);
    setTimerIsActive(false);
    setElapsedTime(0);
    setTargetWordId(null);
    setDisplayMode(prev => (prev === 'single' ? 'multi' : 'single'));
  };

  const handleFlipAll = (direction) => {
    if (displayMode !== 'multi' || currentCategoryWords.length === 0) return;
    const newFlippedValue = direction === 'down';
    const categoryUpdates = currentCategoryWords.reduce((acc, word) => {
      acc[word.id] = newFlippedValue;
      return acc;
    }, {});
    setFlippedStates(prev => ({ ...prev, ...categoryUpdates }));
  };

  // --- NEW: Handle Card Click (Branching Logic for Selection Mode) ---
  const handleCardClick = (wordId) => {
    if (isSelectionMode) {
      setSelectedWordIds(prev => {
        if (prev.includes(wordId)) {
          return prev.filter(id => id !== wordId);
        } else {
          return [...prev, wordId];
        }
      });
    } else {
      setFlippedStates(prev => ({ ...prev, [wordId]: !prev[wordId] }));
    }
  };

  // --- NEW: Toggle Make Set Mode ---
  const handleToggleMakeSet = () => {
    if (isSelectionMode) {
      // We are currently in selection mode and user clicked "Send Set"
      if (selectedWordIds.length === 0) {
        alert("Please select at least one card first.");
        return;
      }

      // Generate Data Payload
      const setPayload = {
        name: new Date().toISOString().split('T')[0], // Default name: Date
        words: selectedWordIds
      };

      // Convert to JSON string for QR
      setQrData(JSON.stringify(setPayload));
      setShowQRModal(true);

    } else {
      // Turn ON selection mode
      setIsSelectionMode(true);
      setIsSpeakingRandomly(false); // Stop any active games
      setTimerIsActive(false);
      // Ensure cards are face up so we can see what we are selecting
      handleFlipAll('up');
    }
  };

  const handleCloseQR = () => {
    setShowQRModal(false);
    setIsSelectionMode(false);
    setSelectedWordIds([]);
  };

  const toggleSpeechSpeed = () => setSpeechSpeed(prev => (prev === 'normal' ? 'slow' : 'normal'));
  const toggleShuffle = () => setIsShuffled(prev => !prev);
  const handleToggleCategory = (catId) => setActiveCategories(p => p.includes(catId) ? p.filter(c => c !== catId) : [...p, catId]);
  const handleJumpToCategory = (catId) => {
    const firstWordIndex = displayWords.findIndex(word => word.category === catId);
    if (firstWordIndex !== -1) setCurrentIndex(firstWordIndex);
    setIsMenuOpen(false);
  };

  const currentWord = displayWords[currentIndex];

  // ========= START: src/components/TangoPracticeScreen.js - RENDER SECTION WITH BG IMAGE =========
  return (
    <>
      {/* QR Modal */}
      {showQRModal && (
        <div className="modal-overlay">
          <div className="qr-modal-content">
            <button className="modal-close-x" onClick={handleCloseQR}>&times;</button>
            <h2>Scan to Get Set</h2>
            <div className="qr-canvas-container">
              <QRCodeCanvas value={qrData} size={256} level={"L"} />
            </div>
            <p>Students can scan this code to instantly load these {selectedWordIds.length} cards.</p>
            <button onClick={handleCloseQR} className="practice-nav-button">Close</button>
          </div>
        </div>
      )}

      <SideMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        categoriesToDisplay={practiceCategoriesDetails}
        activeCategories={activeCategories}
        onToggleCategory={handleToggleCategory}
        onJumpToCategory={handleJumpToCategory}
      />

      {/* UPDATED: Added inline style for the background image */}
      <div
        className="tango-practice-screen"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/backgrounds/practice_background.jpg)` }}
      >
        <header className="practice-header">
          <div className="header-left">
            <button onClick={onEndPractice} className="practice-nav-button">&lt; Back</button>
            <button onClick={() => setIsMenuOpen(true)} className="practice-nav-button">Menu</button>

            <button
              onClick={handleToggleMakeSet}
              className={`practice-nav-button ${isSelectionMode ? 'send-set-button' : 'make-set-button'}`}
            >
              {isSelectionMode ? 'Send Set 🚀' : 'Make Set'}
            </button>

            <button onClick={toggleShuffle} className={`practice-nav-button shuffle-button ${isShuffled ? 'active' : ''}`} disabled={isSelectionMode}>Shuffle: {isShuffled ? 'ON' : 'OFF'}</button>
            <button onClick={handleToggleMultiMode} className={`practice-nav-button multi-button ${displayMode === 'multi' ? 'active' : ''}`}>Multi</button>

            {displayMode === 'multi' && !isSelectionMode && (
              <>
                <button onClick={() => handleFlipAll('up')} className="practice-nav-button">Flip ⬆️</button>
                <button onClick={() => handleFlipAll('down')} className="practice-nav-button">Flip ⬇️</button>
              </>
            )}
          </div>
          <div className="header-right">
            {displayMode === 'multi' && (<div className="timer-display">{formatTime(elapsedTime)}</div>)}
            <button onClick={toggleSpeechSpeed} className="practice-nav-button speed-toggle">Speed: {speechSpeed === 'normal' ? 'Normal' : 'Slow'}</button>
            <button
              onClick={handleSayWord}
              className={`practice-nav-button say-word-button ${isSpeakingRandomly ? 'active' : ''}`}
              disabled={isSelectionMode}
            >
              <img src={`${process.env.PUBLIC_URL}/assets/buttons/say_word.png`} alt="Say Word" />
            </button>
          </div>
        </header>

        {displayMode === 'single' ? (
          <div className="card-display-area single-card-view">
            {currentWord ? (
              <div className={`single-card-wrapper ${selectedWordIds.includes(currentWord.id) ? 'selected' : ''}`}>
                <Flashcard
                  wordData={currentWord}
                  isFlipped={!!flippedStates[currentWord.id]}
                  onClick={() => handleCardClick(currentWord.id)}
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedWordIds.includes(currentWord.id)}
                  viewMode="single"
                />
                <p className={`flashcard-text-external ${flippedStates[currentWord.id] ? 'hidden' : ''}`}>{currentWord.word}</p>
              </div>
            ) : (<div className="no-words-placeholder"><p>No words to display.</p></div>)}
          </div>
        ) : (
          <div ref={multiViewContainerRef} className="card-display-area multi-card-view" style={{ gridTemplateColumns: dynamicGridStyles.gridTemplateColumns }}>
            {currentCategoryWords.map(word => (
              <div key={word.id} className={`multi-card-wrapper ${selectedWordIds.includes(word.id) ? 'selected' : ''}`}>
                <div style={{ width: dynamicGridStyles.cardWidth, height: dynamicGridStyles.cardHeight }}>
                  <Flashcard
                    wordData={word}
                    isFlipped={!!flippedStates[word.id]}
                    onClick={() => handleCardClick(word.id)}
                    isSelectionMode={isSelectionMode}
                    isSelected={selectedWordIds.includes(word.id)}
                    viewMode="multi"
                  />
                </div>
                <DynamicTextLabel
                  text={word.word}
                  className="flashcard-text-external small"
                  containerWidth={dynamicGridStyles.cardWidth}
                  maxLabelHeight={dynamicGridStyles.maxLabelHeight}
                />
              </div>
            ))}
          </div>
        )}

        {displayMode === 'single' && (
          <footer className="practice-footer">
            <button
              onClick={handlePrevious}
              className="practice-arrow-button"
              disabled={displayWords.length === 0}
            >
              <img src={`${process.env.PUBLIC_URL}/assets/buttons/previous_card.png`} alt="Previous" />
            </button>
            <div className="progress-indicator">
              {displayWords.length > 0 ? `${currentIndex + 1} / ${displayWords.length}` : '0 / 0'}
            </div>
            <button
              onClick={handleNext}
              className="practice-arrow-button"
              disabled={displayWords.length === 0}
            >
              <img src={`${process.env.PUBLIC_URL}/assets/buttons/next_card.png`} alt="Next" />
            </button>
          </footer>
        )}
      </div>
    </>
  );
}

export default TangoPracticeScreen;
// ========= END: src/components/TangoPracticeScreen.js - RENDER SECTION WITH BG IMAGE =========